import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getSwitchDetails, getSwitchTimeline } from "./SwitchDetails.actions";
import { useParams, useNavigate } from "react-router-dom";
import {
  resetSubmittedDisposition,
  resetEventRequest,
} from "views/Raw/SwitchDisposition/SwitchDisposition.actions";
import ActivitiesPopup from "components/MemberModule/ActivitiesPopup/ActivitiesPopup";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import cloneDeep from "lodash/cloneDeep";

import "./SwitchDetails.css";
import "assets/css/ribbon.css";
import {
  SWIM_HISTORY_STATE,
  SWIM_STATE,
  NOT_STARTED_STATUS_WITH_HISTORY_OF_CHUNK,
} from "lookup/SwimConstants";
import DismissableModal from "components/DismissableModal";
import EntityDetails from "components/SwitchDetails/EntityDetails";
import ProjectedSavings from "components/SwitchDetails/ProjectedSavings";
import OriginalClaim from "components/SwitchDetails/OriginalClaim";
import CustomerAndClientTitle from "components/CustomerAndClientTitle";
import MedicineHistory from "components/SwitchDetails/MedicineHistory";
import SwitchStateChanger from "components/SwitchDetails/SwitchStateChanger/SwitchStateChanger";
import Notes from "components/SwitchDetails/Notes";
import SwitchTimelineInfo from "components/SwitchDetails/SwitchTimelineInfo";
import SwitchTimeline from "components/SwitchDetails/SwitchTimeline";
import { Button } from "react-bootstrap";
import { WorkflowConstants } from "lookup/WorkflowConstants";
import { NotesFilterConstants } from "lookup/NotesFilterConstants";
import BatchOrCycle from "components/BatchOrCycle";
import CloseSwitchModal from "components/SwitchDetails/CloseSwitchModal";
import { getErrorMessage } from "services/UtilityService";
import CreateActivity from "components/SwitchDetails/CreateActivity";
import { showNotification } from "services/NotificationService";
import ChangeSwitchTemplate from "components/SwitchDetails/ChangeSwitchTemplate";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";

export const prepareTimelineForDisplay = (timeline) => {
  if (timeline && timeline.length > 1) {
    const history = timeline.filter(
      (element) => element.title.toUpperCase() === SWIM_HISTORY_STATE
    );
    const historyEvents = history.length > 0 ? history[0].events : [];

    if (historyEvents && historyEvents.length > 0) {
      let map = new Map();
      historyEvents.forEach((element) => {
        if (map.has(element.uiState.toUpperCase())) {
          map.get(element.uiState.toUpperCase()).push(element);
        } else map.set(element.uiState.toUpperCase(), [element]);
      });

      const newTimeline = timeline.filter(
        (element) => !(element.title.toUpperCase() === SWIM_HISTORY_STATE)
      );

      newTimeline.forEach((element, index) => {
        if (element.title) {
          if (map.has(element.title.toUpperCase())) {
            const chunkHistory = map.get(element.title.toUpperCase());
            let chunkEventHistory = cloneDeep(history[0]);
            chunkEventHistory.events = cloneDeep(chunkHistory);
            element.history = cloneDeep(chunkEventHistory);
            if (element.state === SWIM_STATE.InActive) {
              element.state = SWIM_STATE.History;
              element.lastEvent = {
                status: NOT_STARTED_STATUS_WITH_HISTORY_OF_CHUNK,
                date: chunkEventHistory.lastEvent.date,
              };
            }
          }
        }
      });

      return newTimeline;
    }
  }
  return timeline;
};

const SwitchDetails = () => {
  const dispatch = useDispatch();
  const { wfItemId } = useParams();
  const navigate = useNavigate();
  const securePdfModalRef = useRef(null);

  const {
    switchData,
    loading,
    switchTimeline,
    timelineLoading,
    showDispositionSuccessNotification,
    showEventSuccessNotification,
  } = useSelector((state) => ({
    switchData: state.switchDetails.switchData,
    loading: state.switchDetails.loading,
    switchTimeline: state.switchDetails.switchTimeline,
    timelineLoading: state.switchDetails.timelineLoading,
    showDispositionSuccessNotification: state.switchDisposition.submittedDisposition,
    showEventSuccessNotification: state.switchDisposition.eventRequestCompleted,
  }));

  const [shouldShowActivityPopup, setShouldShowActivityPopup] = useState(false);

  const fetchData = useCallback(() => {
    dispatch(getSwitchTimeline(wfItemId));
    dispatch(getSwitchDetails(wfItemId));
  }, [dispatch, wfItemId]);

  useEffect(() => {
    if (showDispositionSuccessNotification) {
      showNotification({
        title: "Successfully updated Switch Disposition!",
        message: "The switch disposition has been updated for this switch.",
        position: "bl",
        type: "success",
      });
      dispatch(resetSubmittedDisposition());
    }
  }, [showDispositionSuccessNotification, dispatch]);

  useEffect(() => {
    if (showEventSuccessNotification) {
      showNotification({
        title: "Event Executed!",
        message: "The event has been executed on RAW.",
        position: "bl",
        type: "success",
      });
      dispatch(resetEventRequest());
    }
  }, [showEventSuccessNotification, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showErrorNotification = (title, responseData) => {
    showNotification({
      title: title,
      message:
        getErrorMessage(responseData) ||
        getErrorMessage("SOMETHING_WENT_WRONG"),
      position: "tr",
      type: "error",
    });
  };

  const changeActivityPopupVisibility = () => {
    setShouldShowActivityPopup((prev) => !prev);
  };

  const redirectToLinkFaxDetailsPage = (closeModal, faxInfoIdentifier) => {
    closeModal();
    navigate(`/unprocessed-fax/fax-details/${faxInfoIdentifier}`);
  };

  const handleButtonClicks = (switchWfItemId) => {
    navigate(`/switch/${switchWfItemId}`);
    // The fetchData in the useEffect of the new page will handle this
  };

  if (loading || timelineLoading) {
    return "Loading ...";
  } else if (
    (!loading && !switchData) ||
    (!timelineLoading && !switchTimeline)
  ) {
    return "Error while fetching switch data";
  }

  const switchInfo = switchData.switchInfo;
  const isPrescriberResponseFaxAvailable =
    switchData.isPrescriberResponseFaxAvailable;
  const timeline = prepareTimelineForDisplay(switchTimeline.timeline);
  const workflowRestarted = timeline[0].history ? true : false;
  const fillDetails = switchData.fillDetails;
  const faxInfoIdentifier = switchInfo.faxInfoIdentifier || "";
  const includeNotifyBadge = switchData?.isMemberNotified || false;
  const isHsc = switchData?.isHsc || false;
  const customerId = switchInfo.rirCustomerInfo.customerId;
  const switchId = switchInfo.rirSwitchId;

  const customFooterContent = (
    <div>
      <Button
        id="redacted-pdf-secure-viewer-close"
        variant="outline-dark"
        onClick={() => securePdfModalRef.current?.handleClose()}
      >
        Close
      </Button>
    </div>
  );

  return (
    <Container
      className={`bottom-spacer ${
        switchData.status === "ON_HOLD" ? "held-switch ribbon-container" : ""
      }`}
      fluid
    >
      {switchData.status === "ON_HOLD" ? (
        <div className="ribbon ribbon-red ribbon-top-left">
          <span>on hold</span>
        </div>
      ) : null}
      <Row>
        <Col md={6}>
          <SwitchTimelineInfo
            state={switchData.state}
            status={switchData.status}
            wfItemId={switchData.wfItemId}
            workflowRestarted={workflowRestarted}
          />
          <SwitchTimeline
            rawId={switchData.rawId}
            wfItemId={wfItemId}
            switchHistory={timeline}
            isPrescriberResponseFaxAvailable={isPrescriberResponseFaxAvailable}
            fillDetails={fillDetails}
            switchId={switchInfo.rirSwitchId}
            switchStatus={switchData.status}
            switchState={switchData.state}
            onStateChange={fetchData}
          />
          <Row className="bottom-spacer">
            <Col md={6}>
              <CloseSwitchModal
                wfItemId={wfItemId}
                closestChainSwitchId={switchData.closestChainSwitchGlobalId}
                isActionCallable={
                  switchData.status !==
                    WorkflowConstants.MISSING_PRESCRIBER_CONTACT_INFO &&
                  switchData.status !==
                    WorkflowConstants.DELIVERED_PRESCRIBER_FAX &&
                  switchData.status !==
                    "ERROR_PUTTING_ON_NO_PRESCRIBER_RESPONSE" &&
                  switchData.status !==
                    "ERROR_PUTTING_ON_PRESCRIBER_CONTACT" &&
                  switchData.status !== "GENERATED_PDF" &&
                  switchData.status !== "ON_HOLD"
                }
                onEventExecuted={fetchData}
              />
            </Col>
            <Col md={2}>
              <SwitchStateChanger
                wfItemId={wfItemId}
                switchId={switchInfo.rirSwitchId}
                event={switchData.status === "ON_HOLD" ? "RESUME" : "HOLD"}
                onEventExecuted={fetchData}
              />
            </Col>
            <Col md={4}>
              <SwitchStateChanger
                wfItemId={wfItemId}
                switchId={switchInfo.rirSwitchId}
                event={switchData.status ===
                  WorkflowConstants.MISSING_PRESCRIBER_CONTACT_INFO ? "PRESCRIBER_CONTACT_INFO_AVAILABLE" : "GENERATE_PDF"}
                onEventExecuted={fetchData}
              />
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <div className="d-flex justify-content-between">
            <Button
              size="sm"
              variant="secondary"
              className="transition-btn btn-text-white"
              onClick={() => handleButtonClicks(switchData.predecessorWfItemId)}
              disabled={!switchData.predecessorWfItemId}
            >
              Predecessor
            </Button>
            <CustomerAndClientTitle
              globalSwitchId={switchInfo.globalSwitchId}
              customerName={
                switchInfo.rirCustomerInfo
                  ? switchInfo.rirCustomerInfo.customerName
                  : null
              }
              clientName={
                switchInfo.clientInfo ? switchInfo.clientInfo.clientName : null
              }
              customerDisplayId={
                switchInfo.rirCustomerInfo
                  ? switchInfo.rirCustomerInfo.customerDisplayId
                  : null
              }
              clientDisplayId={
                switchInfo.clientInfo
                  ? switchInfo.clientInfo.clientDisplayId
                  : null
              }
              creationDate={switchInfo.creationDate}
              targetSwitchDate={switchInfo.targetSwitchDate}
              isCallActivityCreationBlocked={
                switchData.isCallActivityCreationBlocked
              }
              includeNotifyBadge={includeNotifyBadge}
              isHsc={isHsc}
            />
            <Button
              size="sm"
              variant="secondary"
              className="transition-btn btn-text-white"
              onClick={() => handleButtonClicks(switchData.successorWfItemId)}
              disabled={!switchData.successorWfItemId}
            >
              Successor
            </Button>
          </div>
          <Row className="top-spacer">
            <Col>
              {switchData.batch ? (
                <BatchOrCycle
                  type="Batch"
                  data={switchData.batch}
                  id={switchData.batch.wfBatchId}
                />
              ) : (
                <Badge bg="warning">No batch for Switch</Badge>
              )}
            </Col>
            <Col>
              {switchData.cycle ? (
                <BatchOrCycle
                  type="Cycle"
                  data={switchData.cycle}
                  id={switchData.cycle.wfCycleId}
                />
              ) : (
                <Badge bg="warning">No cycle for Switch</Badge>
              )}
            </Col>
          </Row>
          <Row className="top-spacer">
            <Col md={12}>
              <Notes
                notes={switchData.notes}
                entityId={wfItemId}
                entitySaveUrl={"/wf/note"}
                filtersList={NotesFilterConstants.generalNotesFilter}
                errorNotification={showErrorNotification}
              />
            </Col>
          </Row>

          <Row className="top-spacer">
            <Col md={7}>
              <EntityDetails
                prescriber={
                  switchInfo.prescriberInfo ? switchInfo.prescriberInfo : {}
                }
                pharmacy={switchInfo.pharmaInfo ? switchInfo.pharmaInfo : {}}
                patient={
                  switchInfo.rirMemberInfo ? switchInfo.rirMemberInfo : {}
                }
                globalSwitchId={
                  switchInfo.globalSwitchId ? switchInfo.globalSwitchId : null
                }
                refreshSwim={fetchData}
                wfItemId={wfItemId}
              />
            </Col>
            <Col md={5} className="top-spacer">
              <ProjectedSavings
                paid={switchInfo.currentCost}
                projected={switchInfo.projectedCost}
                savings={switchInfo.projectedSavings}
                hideSavings={!switchInfo.rirCustomerInfo.showSavingPrices || false}
              />
            </Col>
          </Row>
          <Row className="top-spacer">
            <Col md={3} className="d-grid gap-2">
              <CreateActivity
                wfItemId={wfItemId}
                isMemberActivityCreatable={
                  switchData.isMemberActivityCreatable
                }
                disableButton={false}
                fetchData={fetchData}
                selectedPrescriberUuids={
                  switchData.switchInfo.prescriberInfo.prescriberInfoUuid
                    ? [switchData.switchInfo.prescriberInfo.prescriberInfoUuid]
                    : []
                }
                selectedPrescriberNames={
                  switchData.switchInfo.prescriberInfo.prescriberName
                    ? [switchData.switchInfo.prescriberInfo.prescriberName]
                    : []
                }
              />
            </Col>
            <Col md={3} className="d-grid gap-2">
              <OriginalClaim switchInfo={switchInfo} />
            </Col>
            <Col md={3} className="d-grid gap-2">
              <MedicineHistory switchId={switchInfo.rirSwitchId} />
            </Col>
            <Col md={3} className="d-grid gap-2">
              <DismissableModal
                ref={securePdfModalRef}
                customFooterContent={customFooterContent}
                openModalButtonText="Redacted Pdf"
                openModalButtonBlock={true}
                openModalButtonStyle="secondary"
                openModalButtonClassName="btn-text-white"
                title="Redacted Pdf"
                size="lg"
              >
                {
                  <ConfigurablePdfViewer
                    height="70vh"
                    width="100%"
                    url={`/pdf/${customerId}/prescriber-inbound-or-generated-redacted?switch_id=${switchId}`}
                  />
                }
              </DismissableModal>
            </Col>
          </Row>
          <Row className="tiny-top-spacer">
            <Col md={3} className="d-grid gap-2">
              <ChangeSwitchTemplate
                rirSwitchId={switchInfo.rirSwitchId}
                selectedTemplate={switchInfo.templateIndicator}
                disableButton={false}
                fetchData={fetchData}
                showBioSimilarOptions={
                  switchData?.priorityAgent ? switchData.priorityAgent : false
                }
                showDrugDisruptionOptions={
                  switchData?.drugDisruption ? switchData.drugDisruption : false
                }
              />
            </Col>
            <Col md={3} className="d-grid gap-2">
              <Button variant="light" onClick={changeActivityPopupVisibility}>
                Activities
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      {shouldShowActivityPopup && (
        <ActivitiesPopup
          changeActivityPopupVisibility={changeActivityPopupVisibility}
          shouldShowActivityPopup={shouldShowActivityPopup}
          switchId={switchInfo.rirSwitchId}
          isForSwimSwitchList={true}
        />
      )}
    </Container>
  );
};

export default SwitchDetails;
